/** Client-side ALTCHA proof-of-work for native OTP request (SHA-256). */

async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

interface AltchaChallengeJson {
  readonly algorithm?: string;
  readonly challenge?: string;
  readonly salt?: string;
  readonly signature?: string;
  readonly maxnumber?: number;
  readonly maxNumber?: number;
}

function decodeChallenge(encoded: string): AltchaChallengeJson {
  const json = atob(encoded.replace(/-/g, '+').replace(/_/g, '/'));
  return JSON.parse(json) as AltchaChallengeJson;
}

/**
 * Solves a server-issued ALTCHA challenge and returns the base64 payload for POST /otp/request.
 */
export async function solveAltchaChallenge(encodedChallenge: string): Promise<string> {
  const challenge = decodeChallenge(encodedChallenge);
  const algorithm = challenge.algorithm ?? 'SHA-256';
  if (algorithm !== 'SHA-256') {
    throw new Error('Unsupported ALTCHA algorithm');
  }
  const target = challenge.challenge;
  const salt = challenge.salt;
  if (typeof target !== 'string' || typeof salt !== 'string') {
    throw new Error('Invalid ALTCHA challenge');
  }
  const maxNumber =
    typeof challenge.maxNumber === 'number'
      ? challenge.maxNumber
      : typeof challenge.maxnumber === 'number'
        ? challenge.maxnumber
        : 100_000;

  let number = 0;
  for (; number <= maxNumber; number += 1) {
    const hash = await sha256Hex(`${salt}${number}`);
    if (hash === target) {
      const payload = {
        algorithm: 'SHA-256',
        challenge: target,
        number,
        salt,
        signature: challenge.signature,
      };
      return btoa(JSON.stringify(payload));
    }
  }
  throw new Error('ALTCHA solution not found');
}
